import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatSnackBar } from '@angular/material';
import { NgxSpinnerService } from 'ngx-spinner';
import { GatewayService } from 'src/app/gateway.service';
import { LocalSaveService } from 'src/app/services/local-save-service.service';
import { MonitorRealtimeFtthDialogComponent } from './monitor-realtime-ftth-dialog/monitor-realtime-ftth-dialog.component';

type QueryKind = 'HFC' | 'FTTH' | 'TV' | 'TEL';

// TIPO usado por el servicio externo ISOFT cuando se guarda en
// historico_cert_nacimiento. Espejo de la mapeo en certificacion.queries.js
// (1=HFC, 2=FTTH, 3=TV, 4=TELEFONIA).
const TIPO_BY_KIND: { [K in QueryKind]: number } = {
  HFC: 1,
  FTTH: 2,
  TV: 3,
  TEL: 4
};

// Validators del campo "Orden/Falla": 7 chars alfanumericos mayusculas.
// El componente reusable hace el toUpperCase mientras el agente tipea, asi
// que aca solo aseguramos forma final.
const ORDEN_FALLA_PATTERN = /^[A-Z0-9]{7}$/;

@Component({
  selector: 'app-consulta-certificado-nacimieto',
  templateUrl: './consulta-certificado-nacimieto.component.html',
  styleUrls: ['./consulta-certificado-nacimieto.component.scss']
})
export class ConsultaCertificadoNacimietoComponent implements OnInit {

  public loading: { [K in QueryKind]: boolean } = {
    HFC: false,
    FTTH: false,
    TV: false,
    TEL: false
  };

  public hfcResult: any = null;
  public ftthResult: any = null;
  public tvResult: any = null;
  public telephonyResult: any = null;

  public formHFC: FormGroup = new FormGroup({
    deviceId: new FormControl('', [Validators.required]),
    ordenFalla: new FormControl('', [Validators.required, Validators.pattern(ORDEN_FALLA_PATTERN)])
  });

  public formFTTH: FormGroup = new FormGroup({
    sn: new FormControl('', [Validators.required]),
    ordenFalla: new FormControl('', [Validators.required, Validators.pattern(ORDEN_FALLA_PATTERN)])
  });

  public formTV: FormGroup = new FormGroup({
    sn: new FormControl('', [Validators.required]),
    ordenFalla: new FormControl('', [Validators.required, Validators.pattern(ORDEN_FALLA_PATTERN)])
  });

  public formTelephony: FormGroup = new FormGroup({
    phone: new FormControl('', [Validators.required]),
    ordenFalla: new FormControl('', [Validators.required, Validators.pattern(ORDEN_FALLA_PATTERN)])
  });

  private getCommonParams() {
    return {
      TECNICO: this.getUserId(),
      TIPO: 2
    };
  }

  // Nota sobre flujo: HFC, TV y Telefonia van al servicio externo de ISOFT,
  // que es quien hace el INSERT en historico_cert_nacimiento. Por eso para esos
  // 3 tipos tenemos que resolver el IDCITA aca en frontend (via lookup contra
  // nuestro backend) y mandarlo en el payload junto con TIPO.
  // FTTH en cambio pega contra nuestro backend, asi que solo le pasamos
  // ordenFalla y el backend resuelve IDCITA por su cuenta.
  private queryConfig: any = {
    HFC: {
      form: () => this.formHFC,
      buildParams: (idcita) => ({
        deviceId: this.formHFC.controls.deviceId.value,
        IDCITA: idcita,
        TIPO: TIPO_BY_KIND.HFC,
        ORDEN_FALLA: this.formHFC.controls.ordenFalla.value,
        ...this.getCommonParams()
      }),
      needsLookup: true,
      request: (params) => this.gateway.certificarHFC(params),
      setResult: (result) => this.hfcResult = result,
      getResult: () => this.hfcResult,
      successMsg: 'Consulta HFC exitosa',
      emptyMsg: 'No se encontraron resultados para HFC',
      errorMsg: 'Error al realizar la consulta HFC'
    },
    FTTH: {
      form: () => this.formFTTH,
      buildParams: (_idcita) => ({
        sn: this.formFTTH.controls.sn.value,
        idusuario: this.getUserId(),
        tipoUsuario: 1,
        // El backend FTTH resuelve idcita internamente (shared/ref_cita.queries).
        ordenFalla: this.formFTTH.controls.ordenFalla.value
      }),
      needsLookup: false,
      request: (params) => this.gateway.certificarFTTH(params),
      setResult: (result) => this.ftthResult = result,
      getResult: () => this.ftthResult,
      successMsg: 'Consulta FTTH exitosa',
      emptyMsg: 'No se encontraron resultados para FTTH',
      errorMsg: 'Error al realizar la consulta FTTH'
    },
    TV: {
      form: () => this.formTV,
      buildParams: (idcita) => ({
        sn: this.formTV.controls.sn.value,
        IDCITA: idcita,
        TIPO: TIPO_BY_KIND.TV,
        ORDEN_FALLA: this.formTV.controls.ordenFalla.value,
        ...this.getCommonParams()
      }),
      needsLookup: true,
      request: (params) => this.gateway.certificarTV(params),
      setResult: (result) => this.tvResult = result,
      getResult: () => this.tvResult,
      successMsg: 'Consulta TV exitosa',
      emptyMsg: 'No se encontraron resultados para TV',
      errorMsg: 'Error al realizar la consulta TV'
    },
    TEL: {
      form: () => this.formTelephony,
      buildParams: (idcita) => ({
        telefono: this.formTelephony.controls.phone.value,
        IDCITA: idcita,
        TIPO: TIPO_BY_KIND.TEL,
        ORDEN_FALLA: this.formTelephony.controls.ordenFalla.value,
        ...this.getCommonParams()
      }),
      needsLookup: true,
      request: (params) => this.gateway.certificarTelefonia(params),
      setResult: (result) => this.telephonyResult = result,
      getResult: () => this.telephonyResult,
      successMsg: 'Consulta Telefonía exitosa',
      emptyMsg: 'No se encontraron resultados para Telefonía',
      errorMsg: 'Error al realizar la consulta Telefonía'
    }
  };

  constructor(
    private gateway: GatewayService,
    private snackBar: MatSnackBar,
    private localSave: LocalSaveService,
    private dialog: MatDialog
  ) { }

  public openMonitorFTTH(): void {
    if (!this.ftthResult) { return; }
    const sn = this.formFTTH.controls.sn.value;
    if (!sn) { return; }
    const eq = this.ftthResult.equipo || {};
    const deviceLabel = eq.name || eq.alias || sn;
    this.dialog.open(MonitorRealtimeFtthDialogComponent, {
      width: '1100px',
      maxWidth: '95vw',
      maxHeight: '90vh',
      data: {
        sn,
        idusuario: this.getUserId(),
        tipoUsuario: 1,
        deviceLabel,
        initialResult: this.ftthResult
      }
    });
  }

  ngOnInit() {
  }

  private getUserId(): string {
    const usuario = this.localSave.get('usuarioPGV');
    if (usuario && usuario.IDU) {
      return usuario.IDU;
    }
    if (usuario && usuario.idagente) {
      return usuario.idagente;
    }
    return '';
  }

  public runQuery(kind: QueryKind): void {
    const cfg = this.queryConfig[kind];

    if (!cfg) { return; }
    if (this.loading[kind]) { return; }

    const form = cfg.form();
    if (!form || form.invalid) { return; }

    this.loading[kind] = true;

    const ordenFalla = form.controls.ordenFalla ? String(form.controls.ordenFalla.value || '') : '';

    // Para HFC/TV/TEL: resolver IDCITA primero contra nuestro backend, luego
    // disparar el certificate al servicio externo con IDCITA + TIPO ya incluidos.
    // Para FTTH: skip lookup (el backend FTTH lo hace internamente).
    const idCitaPromise: Promise<number | null> = cfg.needsLookup
      ? this.lookupIdCita(ordenFalla)
      : Promise.resolve(null);

    idCitaPromise.then((idcita) => {
      const params = cfg.buildParams(idcita);
      cfg.request(params).subscribe(
        (result: any) => {
          this.loading[kind] = false;
          if (result) {
            cfg.setResult(result);
            this.snackBar.open(cfg.successMsg, 'Cerrar', { duration: 3000 });
          } else {
            cfg.setResult(null);
            this.snackBar.open(cfg.emptyMsg, 'Cerrar', { duration: 3000 });
          }
        },
        (_error) => {
          this.loading[kind] = false;
          this.snackBar.open(cfg.errorMsg, 'Cerrar', { duration: 3000 });
        }
      );
    }).catch((_err) => {
      // Si el lookup falla (red/backend) seguimos con idcita=null en vez de
      // bloquear la consulta del agente. El IDCITA del historico quedara vacio.
      const params = cfg.buildParams(null);
      cfg.request(params).subscribe(
        (result: any) => {
          this.loading[kind] = false;
          if (result) { cfg.setResult(result); this.snackBar.open(cfg.successMsg, 'Cerrar', { duration: 3000 }); }
          else { cfg.setResult(null); this.snackBar.open(cfg.emptyMsg, 'Cerrar', { duration: 3000 }); }
        },
        (_error) => {
          this.loading[kind] = false;
          this.snackBar.open(cfg.errorMsg, 'Cerrar', { duration: 3000 });
        }
      );
    });
  }

  // Llama al endpoint /api/PGV/CERTIFICACION_TELEFONIA/LOOKUP_IDCITA que
  // resuelve un IDCITA a partir del Orden/Falla (numero_orden en ref_cita,
  // ultimos 10 dias). Devuelve null si no hay match — el llamador es libre
  // de continuar el flujo con idcita=null.
  private lookupIdCita(ordenFalla: string): Promise<number | null> {
    if (!ordenFalla) { return Promise.resolve(null); }
    return new Promise((resolve, reject) => {
      this.gateway.get('lookupIdCitaByOrden', { ordenFalla: ordenFalla })
        .then((res: any) => {
          if (res && (res.idcita !== undefined && res.idcita !== null)) {
            resolve(Number(res.idcita));
          } else {
            resolve(null);
          }
        })
        .catch((err: any) => reject(err));
    });
  }

  public clearQuery(kind: QueryKind): void {
    const cfg = this.queryConfig[kind];
    if (!cfg) { return; }

    cfg.setResult(null);
    const form = cfg.form();
    if (form) { form.reset(); }
  }

  // --- HFC formatted text getters ---

  public getHfcProvisioningText(): string {
    if (!this.hfcResult || !this.hfcResult.provisioning) { return ''; }
    let p = this.hfcResult.provisioning;
    let lines = [];
    lines.push('--- Provisionamiento ---');
    if (p.regStatus) { lines.push('Estado: ' + p.regStatus); }
    if (p.cmProvisioned === true || p.cmProvisioned === 'true') {
      lines.push('Provisionado');
    } else {
      lines.push('No provisionado');
    }
    return lines.join('\n');
  }

  public getHfcSoftwareText(): string {
    if (!this.hfcResult || !this.hfcResult.software) { return ''; }
    const s = this.hfcResult.software;
    let lines = [];
    lines.push('--- Software ---');
    if (s.sw_string) { lines.push(s.sw_string); }
    if (s.pass === true || s.pass === 1) {
      lines.push('Version correcta');
    } else if (s.pass === false || s.pass === 0) {
      lines.push('Version incorrecta');
    }
    return lines.join('\n');
  }

  public getHfcResumenText(): string {
    if (!this.hfcResult) { return ''; }
    let lines = [];
    lines.push('--- Resumen ---');
    lines.push(this.hfcResult.resumen || this.hfcResult.summary || '');
    return lines.join('\n');
  }

  public copyAllHFC(): void {
    if (!this.hfcResult) { return; }
    let sections = [
      this.getHfcProvisioningText(),
      this.getHfcSoftwareText(),
      this.getHfcResumenText()
    ].filter(function(s) { return s && s.length > 0; });
    this.copyToClipboard(sections.join('\n\n'), 'Todos los resultados HFC copiados al portapapeles');
  }

  // --- FTTH formatted text getters ---

  public getFtthTipoEquipoText(): string {
    if (!this.ftthResult || !this.ftthResult.parametros) { return ''; }
    const p = this.ftthResult.parametros;
    const eq = this.ftthResult.equipo || {};
    const lines = [];
    lines.push('--- Estado del equipo ---');
    if (eq.name)  lines.push('Name: '  + eq.name);
    if (eq.alias) lines.push('Alias: ' + eq.alias);
    const fmt = (label, k) => {
      if (!p[k]) return null;
      return label + ': ' + (p[k].valor || '-') + (p[k].pass ? ' (OK)' : ' (FALLA, esperado=' + (p[k].esperado || '') + ')');
    };
    [fmt('Operation status', 'admin-status'), fmt('ontstat', 'ontstat'), fmt('cfgstat', 'cfgstat')]
      .filter(s => !!s).forEach(s => lines.push(s));
    if (p['soft-version'] && p['soft-version'].valor) {
      lines.push('Version de software: ' + p['soft-version'].valor + (p['soft-version'].pass ? ' (OK)' : ' (FALLA)'));
    }
    return lines.join('\n');
  }

  public getFtthPerfilesText(): string {
    if (!this.ftthResult || !this.ftthResult.parametros) { return ''; }
    let p = this.ftthResult.parametros;
    let lines = [];
    lines.push('--- Perfiles Asociados ---');
    if (p.lineprof && p.lineprof.valor) { lines.push('Line Profile: ' + p.lineprof.valor); }
    if (p.servprof && p.servprof.valor) { lines.push('Service Profile: ' + p.servprof.valor); }
    if (p.vaprof && p.vaprof.valor) { lines.push('ONU General VAS Profile: ' + p.vaprof.valor); }
    return lines.join('\n');
  }

  public getFtthOpticosText(): string {
    if (!this.ftthResult || !this.ftthResult.parametros) { return ''; }
    let p = this.ftthResult.parametros;
    let lines = [];
    lines.push('--- Parametros Opticos ---');
    if (p.opticsrxpower && p.opticsrxpower.valor_dBm !== undefined) {
      lines.push('RX Power: ' + p.opticsrxpower.valor_dBm);
      if (p.opticsrxpower.rango_min !== undefined) {
        lines.push('(rango permitido: ' + p.opticsrxpower.rango_min + ' a ' + p.opticsrxpower.rango_max + ')');
      }
    }
    if (p.opticstxpower && p.opticstxpower.valor_dBm !== undefined) {
      lines.push('TX Power: ' + p.opticstxpower.valor_dBm);
      if (p.opticstxpower.rango_min !== undefined) {
        lines.push('(rango permitido: ' + p.opticstxpower.rango_min + ' a ' + p.opticstxpower.rango_max + ')');
      }
    }
    return lines.join('\n');
  }

  public getFtthResumenText(): string {
    if (!this.ftthResult) { return ''; }
    let lines = [];
    lines.push('--- Resumen ---');
    lines.push(this.ftthResult.resumen || this.ftthResult.summary || '');
    return lines.join('\n');
  }

  public getFtthVlansText(): string {
    if (!this.ftthResult) { return ''; }
    let lines = [];
    lines.push('--- VLANs ---');
    const vlans = this.ftthResult.vlans || [];
    for (let i = 0; i < vlans.length; i++) {
      const v = vlans[i];
      lines.push(
        'VLAN ' + (v.vlanid || '?') +
        ' | ' + (v.interface_selection || '') +
        ' | uv=' + (v.uv || '') + ' (esperada=' + (v.user_vlan_esperada || '-') + ')' +
        ' | tx=' + (v.tx || '') + ' (esperado=' + (v.traffic_profile_tx_esperado || '-') + ')' +
        ' rx=' + (v.rx || '') + ' (esperado=' + (v.traffic_profile_rx_esperado || '-') + ')' +
        (v.pass ? ' OK' : ' FALLA')
      );
    }
    return lines.join('\n');
  }

  public copyAllFTTH(): void {
    if (!this.ftthResult) { return; }
    let sections = [
      this.getFtthTipoEquipoText(),
      this.getFtthPerfilesText(),
      this.getFtthOpticosText(),
      this.getFtthVlansText(),
      this.getFtthResumenText()
    ].filter(function(s) { return s && s.length > 0; });
    this.copyToClipboard(sections.join('\n\n'), 'Todos los resultados FTTH copiados al portapapeles');
  }

  // --- TV formatted text getters ---

  public getTvParametrosText(): string {
    if (!this.tvResult || !this.tvResult.parametros) { return ''; }
    let p = this.tvResult.parametros;
    let lines = [];
    lines.push('--- Parametros ---');
    if (p.sn && p.sn.valor) { lines.push('Serial Number: ' + p.sn.valor); }
    if (p.ssid && p.ssid.valor !== undefined) { lines.push('SSID: ' + p.ssid.valor); }
    if (p.ipAddr && p.ipAddr.valor !== undefined) { lines.push('IP Address: ' + p.ipAddr.valor); }
    if (p.brand && p.brand.valor !== undefined) { lines.push('Marca: ' + p.brand.valor); }
    if (p.model && p.model.valor !== undefined) { lines.push('Modelo: ' + p.model.valor); }
    if (p.cpuTemp && p.cpuTemp.valor !== undefined) { lines.push('Temperatura CPU: ' + p.cpuTemp.valor); }
    if (p.wifiSignalStrength && p.wifiSignalStrength.valor !== undefined) { lines.push('Señal WiFi: ' + p.wifiSignalStrength.valor); }
    if (p.bluetoothSignalStrength && p.bluetoothSignalStrength.valor !== undefined) { lines.push('Señal Bluetooth: ' + p.bluetoothSignalStrength.valor); }
    if (p.active) {
      if (p.active.pass === true || p.active.pass === 1) { lines.push('Activo: Si'); }
      else { lines.push('Activo: No'); }
      if (p.active.valor !== undefined) { lines.push('Valor: ' + (p.active.valor == true ? 'Si' : 'No')); }
      if (p.active.esperado !== undefined) { lines.push('Esperado: ' + (p.active.esperado == true ? 'Si' : 'No')); }
    }
    if (p.lock) {
      if (p.lock.pass === true || p.lock.pass === 1) { lines.push('Bloqueado: Correcto'); }
      else { lines.push('Bloqueado: Incorrecto'); }
      if (p.lock.valor !== undefined) { lines.push('Valor: ' + (p.lock.valor == true ? 'Si' : 'No')); }
      if (p.lock.esperado !== undefined) { lines.push('Esperado: ' + (p.lock.esperado == true ? 'Si' : 'No')); }
    }
    return lines.join('\n');
  }

  public getTvResumenText(): string {
    if (!this.tvResult) { return ''; }
    let lines = [];
    lines.push('--- Resumen ---');
    lines.push(this.tvResult.resumen || this.tvResult.summary || '');
    return lines.join('\n');
  }

  public copyAllTV(): void {
    if (!this.tvResult) { return; }
    let sections = [
      this.getTvParametrosText(),
      this.getTvResumenText()
    ].filter(function(s) { return s && s.length > 0; });
    this.copyToClipboard(sections.join('\n\n'), 'Todos los resultados TV copiados al portapapeles');
  }

  // --- Telephony formatted text getters ---

  public getTelParametrosText(): string {
    if (!this.telephonyResult || !this.telephonyResult.parametros) { return ''; }
    let p = this.telephonyResult.parametros;
    let lines = [];
    lines.push('--- Parametros ---');
    if (p.SIP_DN && p.SIP_DN.valor !== undefined) { lines.push('SIP DN: ' + p.SIP_DN.valor); }
    if (p.SIP_URI && p.SIP_URI.valor !== undefined) { lines.push('SIP URI: ' + p.SIP_URI.valor); }
    if (p.SIP_TIMEZONE && p.SIP_TIMEZONE.valor !== undefined) { lines.push('Timezone: ' + p.SIP_TIMEZONE.valor); }
    if (p.SIP_LOCATION && p.SIP_LOCATION.valor !== undefined) { lines.push('Location: ' + p.SIP_LOCATION.valor); }
    if (p.SIP_SUBDOMAIN && p.SIP_SUBDOMAIN.valor !== undefined) { lines.push('Subdomain: ' + p.SIP_SUBDOMAIN.valor); }
    if (p.NUMBER_OF_CONTACTS && p.NUMBER_OF_CONTACTS.valor !== undefined) { lines.push('Número de Contactos: ' + p.NUMBER_OF_CONTACTS.valor); }
    if (p.CONTACTS && p.CONTACTS.valor !== undefined) { lines.push('Contactos: ' + p.CONTACTS.valor); }
    return lines.join('\n');
  }

  public getTelValidacionesText(): string {
    if (!this.telephonyResult || !this.telephonyResult.parametros) { return ''; }
    const p = this.telephonyResult.parametros;
    let lines = [];
    lines.push('--- Validaciones ---');
    const validations = [
      { key: 'SIP_FIRST_NAME', label: 'Sip First Name' },
      { key: 'SIP_LAST_NAME', label: 'SIP Last Name' },
      { key: 'SIP_EP_NAME', label: 'SIP EP Name' },
      { key: 'ACCOUNT_STATUS', label: 'Account Status' },
      { key: 'REGISTERED', label: 'Registrado' }
    ];
    for (let i = 0; i < validations.length; i++) {
      const v = validations[i];
      const param = p[v.key];
      if (param) {
        if (param.pass === true || param.pass === 1) {
          lines.push(v.label + ': Correcto');
        } else if (param.pass === false || param.pass === 0) {
          lines.push(v.label + ': Incorrecto');
        }
        if (param.valor !== undefined) { lines.push('Valor: ' + param.valor); }
        if (param.esperado !== undefined) { lines.push('Esperado: ' + param.esperado); }
      }
    }
    return lines.join('\n');
  }

  public getTelResumenText(): string {
    if (!this.telephonyResult) { return ''; }
    const lines = [];
    lines.push('--- Resumen ---');
    lines.push(this.telephonyResult.resumen || this.telephonyResult.summary || '');
    return lines.join('\n');
  }

  public copyAllTel(): void {
    if (!this.telephonyResult) { return; }
    const sections = [
      this.getTelParametrosText(),
      this.getTelValidacionesText(),
      this.getTelResumenText()
    ].filter(function(s) { return s && s.length > 0; });
    this.copyToClipboard(sections.join('\n\n'), 'Todos los resultados Telefonía copiados al portapapeles');
  }

  // --- Shared clipboard helper ---

  private copyToClipboard(text: string, msg: string): void {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    this.snackBar.open(msg, 'Cerrar', { duration: 2000 });
  }
}
